
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FeedbackResponseForm } from '@/components/feedback';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { validateFeedbackToken } from '@/utils/tokenValidator';
import { supabase } from '@/integrations/supabase/client';

const Feedback = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [requestExists, setRequestExists] = useState(false);
  
  useEffect(() => {
    const validateToken = async () => {
      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get('token');
      
      if (token) {
        // Validate the token
        const result = await validateFeedbackToken(token);
        if (!result.isValid) {
          setValidationError(result.error || 'Token invalide ou expiré');
          return;
        }
      }
    };
    
    validateToken();
  }, [location.search]);

  useEffect(() => {
    const checkRequestExists = async () => {
      if (!requestId) {
        setValidationError("Identifiant de demande manquant");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Check first in feedback_requests
        const { data: standardRequest, error: standardError } = await supabase
          .from('feedback_requests')
          .select('id')
          .eq('id', requestId)
          .maybeSingle();
          
        if (standardError) throw standardError;
        
        // If not found, check in candidate_to_recruiter_feedback_requests
        if (!standardRequest) {
          const { data: candidateRequest, error: candidateError } = await supabase
            .from('candidate_to_recruiter_feedback_requests')
            .select('id')
            .eq('id', requestId)
            .maybeSingle();
            
          if (candidateError) throw candidateError;
          
          if (candidateRequest) {
            setRequestExists(true);
          } else {
            setValidationError("Demande de feedback introuvable");
          }
        } else {
          setRequestExists(true);
        }
      } catch (error) {
        console.error("Error checking feedback request:", error);
        setValidationError("Erreur lors de la vérification de la demande");
      } finally {
        setLoading(false);
      }
    };
    
    checkRequestExists();
  }, [requestId]);
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-gray-50 py-4 md:py-8">
        <div className="container mx-auto px-3 md:px-4">
          <Button
            variant="ghost"
            className="mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : validationError ? (
            <div className="max-w-xl mx-auto">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Erreur d'accès</AlertTitle>
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            </div>
          ) : requestExists && requestId ? (
            <FeedbackResponseForm requestId={requestId} />
          ) : (
            <div className="max-w-xl mx-auto">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Erreur</AlertTitle>
                <AlertDescription>Demande de feedback introuvable</AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Feedback;
